import type { ReactNode } from 'react'
import DateInput from '@/components/DateInput'
import { HoverHelp } from '@/tableControls'
import type {
  DistributionHistoryResponse,
  FloorAnalysisResponse,
  ModelSummaryResponse,
} from '@/services/projections'
import type { CaseInputs, CashPolicy, EvaluateContext, History, SchemaEntry } from '../engine/types'
import { formatCurrencyWhole } from '../projectionsFormat'
import FloorAnalysisPanel, { type FloorAnalysisWindowMonths } from './FloorAnalysisPanel'
import PolicyMoneyInput from './PolicyMoneyInput'
import SegmentedToggle from './SegmentedToggle'
import SolverBlock from './SolverBlock'
import SplitStatus from './SplitStatus'
import YieldLine, { moneyMarketYieldTooltipContent } from './YieldLine'
import PolicyStatusSwitch from './PolicyStatusSwitch'
import { POLICY_CONTROL_WIDTH } from './policyLayout'

type PolicyInputsPanelProps = {
  policy: CashPolicy
  draft: CaseInputs
  schema: SchemaEntry[]
  history: History
  lastClosedMonth: string
  context: EvaluateContext
  model: ModelSummaryResponse
  distHistory: DistributionHistoryResponse | null
  policyEnabled: boolean
  canEditPolicy: boolean
  onPolicyEnabledChange: (enabled: boolean) => void
  floorAnalysis: FloorAnalysisResponse | null
  floorAnalysisLoading: boolean
  floorAnalysisWindowMonths: FloorAnalysisWindowMonths
  cushionDays: number
  yieldStale: boolean
  yieldUnavailable: boolean
  splitWarnings: string[]
  splitRefreshing: boolean
  canEdit: boolean
  mappingWarning?: string | null
  onPolicyChange: (policy: CashPolicy) => void
  onFloorAnalysisWindowMonthsChange: (months: FloorAnalysisWindowMonths) => void
  onCushionDaysChange: (days: number) => void
  onUseRecommendedFloor: (amount: number) => void
  onRefreshSplit: () => void
}

const POLICY_INPUT_CLASS = 'input-sm h-7 w-full !text-xs text-right tabular-nums'

function LabelValueRow({
  label,
  children,
}: {
  label: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="shrink-0 text-xs text-gray-600">{label}</span>
      <div className={`ml-auto shrink-0 text-right text-xs text-gray-900 ${POLICY_CONTROL_WIDTH}`}>
        {children}
      </div>
    </div>
  )
}

const POLICY_SECTION =
  'space-y-2 rounded-lg border border-gray-200 bg-gray-50/80 p-3 [&+&]:mt-1'

const AMOUNT_PER_DISTRIBUTION_HELP =
  'The total distributed each period based on your cadence — every two weeks for biweekly, or once per month for monthly.'

const ONE_TIME_DATE_HELP =
  'Modeled extra; not logged on the Waterfall page. If this date is a regular payout date, it replaces that period’s amount. Remaining dates use Distribution amount.'

const ONE_TIME_AMOUNT_HELP =
  'Cash leaving on the extra date. Solve uses this as already gone, then finds the max sustainable regular amount.'

const ANCHOR_DATE_HELP =
  'The reference distribution date. Projected payouts fall on this date and every cadence interval after it (e.g. every 14 days for biweekly).'

const CADENCE_HELP =
  'How often distributions occur. Biweekly pays on the anchor date and every 14 days; monthly pays once per calendar month.'

const POLICY_OWNED_GRID_HELP =
  "While enabled, Cash – Equivalents and distribution lines are owned by the policy. Scott McClelland's vested Class B share is accrued to Distributions Payable when a real distribution is logged; projections treat the gross amount as cash and hold Distributions Payable flat."

export default function PolicyInputsPanel({
  policy,
  draft,
  schema,
  history,
  lastClosedMonth,
  context,
  model,
  distHistory,
  policyEnabled,
  canEditPolicy,
  onPolicyEnabledChange,
  floorAnalysis,
  floorAnalysisLoading,
  floorAnalysisWindowMonths,
  cushionDays,
  yieldStale,
  yieldUnavailable,
  splitWarnings,
  splitRefreshing,
  canEdit,
  mappingWarning,
  onPolicyChange,
  onFloorAnalysisWindowMonthsChange,
  onCushionDaysChange,
  onUseRecommendedFloor,
  onRefreshSplit,
}: PolicyInputsPanelProps) {
  const floorValue =
    (policy.floor.operating_minimum ?? 0) + (policy.floor.drawdown_allowance ?? 0)
  const mappingIncomplete = model.tier4_shares == null
  const floorUnset = floorValue === 0
  const solverDisabled = mappingIncomplete || floorUnset
  const solverDisabledTooltip = mappingIncomplete
    ? 'Partner mapping required — configure cash_policy.member_partner_map'
    : floorUnset
      ? 'Set a Cash Floor (or click Use recommended floor) before solving.'
      : undefined
  const settings = (model.settings?.cash_policy ?? {}) as Record<string, unknown>
  const solverUpper = Number(settings.solver_upper_bound ?? 250_000)
  const lastDist = distHistory?.items[0]
  const oneTimeDate = policy.distribution.one_time?.date ?? ''
  const oneTimeAmount = policy.distribution.one_time?.amount
  const hasOneTimeAmount =
    oneTimeAmount != null && Number.isFinite(oneTimeAmount) && oneTimeAmount !== 0
  const closedMonthKey = lastClosedMonth.trim().slice(0, 7)
  const oneTimeSpentHelp =
    hasOneTimeAmount && oneTimeDate && oneTimeDate.slice(0, 7) <= closedMonthKey
      ? context.last_dist_date &&
        context.last_dist_num != null &&
        oneTimeDate <= context.last_dist_date
        ? `One-time extra dated ${oneTimeDate} is already in actuals (distribution #${context.last_dist_num} on ${context.last_dist_date}) and is ignored. Clear it.`
        : `One-time extra dated ${oneTimeDate} falls in a closed month and is ignored. Clear it.`
      : null
  const openingDefault =
    history.values.consolidated?.['bs.cash_equivalents']?.[history.nHistory - 1]
  const openingNum =
    typeof openingDefault === 'number' ? openingDefault : Number(openingDefault) || 0

  const patchFloor = (patch: Partial<CashPolicy['floor']>) => {
    onPolicyChange({ ...policy, floor: { ...policy.floor, ...patch } })
  }

  const patchDistribution = (patch: Partial<CashPolicy['distribution']>) => {
    onPolicyChange({ ...policy, distribution: { ...policy.distribution, ...patch } })
  }

  const patchOneTime = (date: string, amount: number | null) => {
    const trimmed = date.trim()
    if (!trimmed && (amount == null || amount === 0)) {
      patchDistribution({ one_time: null })
      return
    }
    patchDistribution({ one_time: { date: trimmed, amount: amount ?? 0 } })
  }

  const patchSweep = (patch: Partial<CashPolicy['sweep']>) => {
    onPolicyChange({ ...policy, sweep: { ...policy.sweep, ...patch } })
  }

  const patchSolver = (patch: Partial<CashPolicy['solver']>) => {
    onPolicyChange({ ...policy, solver: { ...policy.solver, ...patch } })
  }

  return (
    <div className="space-y-5">
      <LabelValueRow
        label={
          <HoverHelp label="Policy status" aria-label="Policy owned grid lines help">
            <p className="text-sm text-gray-600">{POLICY_OWNED_GRID_HELP}</p>
          </HoverHelp>
        }
      >
        <PolicyStatusSwitch
          checked={policyEnabled}
          disabled={!canEditPolicy}
          onChange={onPolicyEnabledChange}
        />
      </LabelValueRow>

      <section className={POLICY_SECTION}>
        <h4 className="text-sm font-medium text-gray-900">Cash Floor</h4>
        <LabelValueRow
          label={
            <HoverHelp label="Cash Floor" aria-label="Cash Floor help">
              <p className="text-sm text-gray-600">
                Month-end cash the projection will not sweep below. Size it from bank statements so
                intra-month dips stay covered.
              </p>
            </HoverHelp>
          }
        >
          <PolicyMoneyInput
            className={POLICY_INPUT_CLASS}
            disabled={!canEdit}
            value={floorValue}
            onChange={(operating_minimum) =>
              patchFloor({ operating_minimum, drawdown_allowance: 0 })
            }
          />
        </LabelValueRow>
        <FloorAnalysisPanel
          data={floorAnalysis}
          loading={floorAnalysisLoading}
          windowMonths={floorAnalysisWindowMonths}
          cushionDays={cushionDays}
          canEdit={canEdit}
          onWindowMonthsChange={onFloorAnalysisWindowMonthsChange}
          onCushionDaysChange={onCushionDaysChange}
          onUseRecommendedFloor={onUseRecommendedFloor}
        />
      </section>

      <section className={POLICY_SECTION}>
        <h4 className="text-sm font-medium text-gray-900">Distributions</h4>
        <div className="space-y-2">
          {lastDist ? (
            <>
              <LabelValueRow label="Last distribution date">
                <span className="tabular-nums">{lastDist.date}</span>
              </LabelValueRow>
              <LabelValueRow label="Last distribution amount">
                <span className="tabular-nums">
                  #{lastDist.num}: {formatCurrencyWhole(Number(lastDist.amount))}
                </span>
              </LabelValueRow>
            </>
          ) : null}
          <LabelValueRow
            label={
              <HoverHelp label="Anchor date" aria-label="Anchor date help">
                <p className="text-sm text-gray-600">{ANCHOR_DATE_HELP}</p>
              </HoverHelp>
            }
          >
            <DateInput
              value={policy.distribution.anchor_date}
              disabled={!canEdit}
              dateFormat="iso"
              className={POLICY_INPUT_CLASS}
              onChange={(value) => patchDistribution({ anchor_date: value })}
            />
          </LabelValueRow>
          <LabelValueRow
            label={
              <HoverHelp label="Cadence" aria-label="Cadence help">
                <p className="text-sm text-gray-600">{CADENCE_HELP}</p>
              </HoverHelp>
            }
          >
            <SegmentedToggle
              fullWidth
              value={policy.distribution.cadence}
              options={[
                { value: 'biweekly', label: 'Biweekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
              onChange={(cadence) => patchDistribution({ cadence })}
              ariaLabel="Distribution cadence"
            />
          </LabelValueRow>
          <LabelValueRow
            label={
              <HoverHelp label="Distribution amount" aria-label="Distribution amount help">
                <p className="text-sm text-gray-600">{AMOUNT_PER_DISTRIBUTION_HELP}</p>
              </HoverHelp>
            }
          >
            <PolicyMoneyInput
              className={POLICY_INPUT_CLASS}
              disabled={!canEdit}
              value={policy.distribution.amount_per_period}
              onChange={(amount_per_period) => patchDistribution({ amount_per_period })}
            />
          </LabelValueRow>
          <LabelValueRow
            label={
              <HoverHelp label="One-time extra amount" aria-label="One-time extra amount help">
                <p className="text-sm text-gray-600">{ONE_TIME_AMOUNT_HELP}</p>
              </HoverHelp>
            }
          >
            <PolicyMoneyInput
              className={POLICY_INPUT_CLASS}
              disabled={!canEdit}
              value={policy.distribution.one_time?.amount ?? null}
              onChange={(amount) =>
                patchOneTime(policy.distribution.one_time?.date ?? '', amount)
              }
            />
          </LabelValueRow>
          {hasOneTimeAmount ? (
            <LabelValueRow
              label={
                <HoverHelp label="One-time extra date" aria-label="One-time extra date help">
                  <p className="text-sm text-gray-600">{ONE_TIME_DATE_HELP}</p>
                </HoverHelp>
              }
            >
              <DateInput
                value={policy.distribution.one_time?.date ?? ''}
                disabled={!canEdit}
                dateFormat="iso"
                className={POLICY_INPUT_CLASS}
                onChange={(value) =>
                  patchOneTime(value, policy.distribution.one_time?.amount ?? null)
                }
              />
              {oneTimeSpentHelp ? (
                <p className="mt-1 text-xs text-gray-500">{oneTimeSpentHelp}</p>
              ) : null}
            </LabelValueRow>
          ) : null}
        </div>
        <SolverBlock
          policy={policy}
          draft={draft}
          schema={schema}
          history={history}
          lastClosedMonth={lastClosedMonth}
          context={context}
          solverUpper={solverUpper}
          disabled={solverDisabled}
          disabledTooltip={solverDisabledTooltip}
          onPolicyChange={(patch) => onPolicyChange({ ...policy, ...patch })}
          onSolverChange={patchSolver}
        />
      </section>

      <section className={POLICY_SECTION}>
        <h4 className="text-sm font-medium text-gray-900">
          <HoverHelp label="Money market" aria-label="Money market yield source">
            {moneyMarketYieldTooltipContent(policy.sweep.yield_snapshot)}
          </HoverHelp>
        </h4>
        {policy.sweep.enabled ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
            The money market is one-way in projections: shortfalls are flagged, never redeemed.
          </div>
        ) : null}
        <LabelValueRow
          label={
            policy.sweep.enabled && (policy.sweep.mode ?? 'fixed') === 'excess' ? (
              <HoverHelp label="Sweep" aria-label="Excess sweep help">
                <p className="text-sm text-gray-600">
                  Excess sweeps cash not needed to stay above the floor for the rest of the projection;
                  at max sustainable this is usually ~$0.
                </p>
              </HoverHelp>
            ) : (
              'Sweep'
            )
          }
        >
          <SegmentedToggle
            fullWidth
            value={
              policy.sweep.enabled ? (policy.sweep.mode ?? 'fixed') : 'off'
            }
            options={[
              { value: 'off', label: 'Off' },
              { value: 'fixed', label: 'Fixed' },
              { value: 'excess', label: 'Excess' },
            ]}
            onChange={(mode) => {
              if (mode === 'off') {
                patchSweep({ enabled: false })
                return
              }
              patchSweep({ mode, enabled: true })
            }}
            ariaLabel="Sweep"
          />
        </LabelValueRow>
        {policy.sweep.enabled && (policy.sweep.mode ?? 'fixed') === 'fixed' ? (
          <LabelValueRow
            label={
              <HoverHelp label="Amount per period" aria-label="Amount per period help">
                <p className="text-sm text-gray-600">
                  Swept on each projected payout date from the anchor date and cadence above — not on
                  payroll dates from the grid.
                </p>
              </HoverHelp>
            }
          >
            <PolicyMoneyInput
              className={POLICY_INPUT_CLASS}
              disabled={!canEdit}
              value={policy.sweep.amount_per_period}
              onChange={(amount_per_period) => patchSweep({ amount_per_period })}
            />
          </LabelValueRow>
        ) : null}
        {policy.sweep.enabled ? (
          <>
            <YieldLine
              snapshot={policy.sweep.yield_snapshot}
              stale={yieldStale}
              unavailable={yieldUnavailable}
            />
            <LabelValueRow label="Opening balance">
              <PolicyMoneyInput
                className={POLICY_INPUT_CLASS}
                disabled={!canEdit}
                value={policy.sweep.opening_mm_balance ?? openingNum}
                onChange={(opening_mm_balance) => patchSweep({ opening_mm_balance })}
              />
            </LabelValueRow>
          </>
        ) : null}
      </section>

      <section className={POLICY_SECTION}>
        <h4 className="text-sm font-medium text-gray-900">Distribution split</h4>
        <SplitStatus
          policy={policy}
          schedule={policy.distribution.split_schedule}
          context={context}
          warnings={splitWarnings}
          mappingWarning={mappingWarning}
          refreshing={splitRefreshing}
          onRefresh={onRefreshSplit}
        />
      </section>
    </div>
  )
}
